import React                 from 'react';
import { MouseEventHandler } from 'react';
import View                  from '@widesk-ui/components/View';
import useTheme              from '@widesk-ui/hooks/useTheme';
import { Action }            from '@widesk-ui/components/ButtonActions';
import ButtonActions         from '@widesk-ui/components/ButtonActions';
import Box                   from '@widesk-ui/components/Box';
import AntdList              from 'antd/lib/list';
import Observer              from '@widesk-ui/components/Observer';
import { ViewProps }         from '@widesk-ui/components/View/viewTypes';
import { BoxProps }          from '@widesk-ui/components/Box';

export type ListItemProps = {
	active?: boolean;
	title?: React.ReactNode;
	subTitle?: React.ReactNode;
	category?: React.ReactNode;
	onClick?: MouseEventHandler;
	onAuxClick?: MouseEventHandler;
	actions?: Action[];
	renderLeft?: () => React.ReactNode;
	renderRight?: () => React.ReactNode;
	mode?: 'card' | 'line';
	className?: string;
	last?: boolean;
} & React.PropsWithChildren & ({
	mode?: 'line';
	extraProps?: ViewProps;
} | {
	mode: 'card';
	extraProps?: BoxProps;
});

export function ListItem(props: ListItemProps) {
	const mode = props.mode || 'line';

	switch (mode) {
		case 'card':
			return <ListItemCard {...props} />;
		case 'line':
			return <ListItemLine {...props} />;
	}
}

const ListItemCard = (props: ListItemProps) => {
	const theme = useTheme();

	return (
		<AntdList.Item>
			<Box
				actions={props.actions}
				bodyStyle={{ padding: theme.marginSM }}
				headStyle={{ paddingLeft: theme.marginSM, paddingRight: theme.marginSM }}
				renderTopRight={props.renderRight}
				dashboardDisabled
				title={(
					<View flex marginV={theme.marginSM}>
						{props.category && <View size={10} wrap color={theme.colorTextLabel}>{props.category}</View>}
						<View bold color={theme.colorTextLabel} lineHeight={1.1}>{props.title}</View>
						{props.subTitle && <View  size={theme.sizeSM} wrap>{props.subTitle}</View>}
					</View>
				)}
				{...props.extraProps as BoxProps}
			>
				{props.children}
			</Box>
		</AntdList.Item>
	);
};

const ListItemLine = (props: ListItemProps) => {
	const theme = useTheme();

	const ref = React.useRef<HTMLDivElement | null>(null);

	const classNames = ['widesk-list-item', props.className];

	React.useEffect(() => {
		if (ref.current && props.active) {
			ref.current?.focus();
		}

	}, [props.active]);

	return (
		<View
			ref={el => ref.current = el}
			row
			spread
			centerV
			gap="xs"
			bg={props.active ? theme.colorPrimaryBgHover : undefined}
			className={classNames.join(' ')}
			onClick={props.onClick}
			onAuxClick={props.onAuxClick}
			paddingV={theme.marginSM}
			paddingH={theme.marginLG}
			separatorB={!props.last}
			transition={false}
			hover={props.onClick ? {
				bg: props.active ? theme.colorPrimaryBgHover : theme.controlItemBgHover,
			} : undefined}
			{...props.extraProps as ViewProps}
		>
			{(!!props.renderLeft) && props.renderLeft()}

			<View gap="xxs" flex={1} overflow="hidden">
				{!!props.title && <div>{props.title}</div>}
				{!!props.subTitle && <div>{props.subTitle}</div>}

				{props.children}
			</View>

			{(!!props.renderRight || !!props.actions) && (
				<Observer render={() => {
					return (
						<View gap="xxs" right>
							{props.renderRight && props.renderRight()}
							{props.actions && <ButtonActions actions={props.actions} size="small" stopPropagation />}
						</View>
					);
				}} />
			)}
		</View>
	);
};
